import type {
  DescService,
  MessageInitShape,
  MessageShape,
} from '@bufbuild/protobuf';
import type { Observable } from 'rxjs';
import { concatMap, EMPTY, of } from 'rxjs';

import type { GrpcNext } from './grpc-next';
import { GrpcOutput } from './grpc-output';
import type { GrpcMetaValue } from './grpc-transport-context';
import { GrpcContext } from './grpc-transport-context';

export abstract class GrpcTransport {
  protected static summon<S extends DescService, M extends keyof S['method']>(
    transport: GrpcTransport,
    service: S,
    methodName: M,
    input: NoInfer<MessageInitShape<S['method'][M]['input']>>,
    context: GrpcContext,
  ): Observable<GrpcNext<S['method'][M]['output']>> {
    return transport.internalSummon<S, M>(service, methodName, input, context);
  }

  protected abstract internalSummon<
    S extends DescService,
    M extends keyof S['method'],
  >(
    service: S,
    methodName: M,
    input: NoInfer<MessageInitShape<S['method'][M]['input']>>,
    context: GrpcContext,
  ): Observable<GrpcNext<S['method'][M]['output']>>;

  public summon<S extends DescService, M extends keyof S['method']>(
    service: S,
    methodName: M,
    input: NoInfer<MessageInitShape<S['method'][M]['input']>>,
    context: GrpcMetaValue<unknown>[] = [],
  ): Observable<MessageShape<S['method'][M]['output']>> {
    return this.internalSummon<S, M>(
      service,
      methodName,
      input,
      GrpcContext.EMPTY.nest(context),
    ).pipe(
      concatMap((next) =>
        next instanceof GrpcOutput ? of(next.message) : EMPTY,
      ),
    );
  }
}
